import { processRoutineForDisplay } from './routineUtils.js';

const processUserDataForDashboard = (userData, dateRange) => {
    const now = new Date();
    const { progress = {}, routine: rawRoutine = {}, goals = [], skinConcerns = [] } = userData || {};

    // Process the raw routine into a 7-day structure ON THE FLY
    const routine = processRoutineForDisplay(rawRoutine);

    const dateToISO = (date) => date.toISOString().split('T')[0];

    const generateDateSeries = (range, endDate) => {
        const series = [];
        const days = range === 'all' ? 90 : parseInt(range.replace('days', ''));
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - days + 1);

        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            series.push(new Date(d));
        }
        return series;
    };

    const dateSeries = generateDateSeries(dateRange, now);
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Calculate adherence from the processed 7-day routine and progress data
    const adherenceData = {};
    dateSeries.forEach(date => {
        const dateStr = dateToISO(date);
        const dayName = daysOfWeek[date.getDay()];
        
        const scheduledRoutine = routine[dayName];
        const loggedProgress = progress[dateStr] || {};

        let totalTasks = 0;
        let completedTasks = 0;

        const morningRoutine = scheduledRoutine?.AM || [];
        const eveningRoutine = scheduledRoutine?.PM || [];
        const weeklyRoutine = scheduledRoutine?.Weekly || []; // This now only contains tasks for the specific day

        totalTasks += morningRoutine.length;
        totalTasks += eveningRoutine.length;
        totalTasks += weeklyRoutine.length;
        
        const loggedAM = loggedProgress.AM || {};
        const loggedPM = loggedProgress.PM || {};
        const loggedWeekly = loggedProgress.Weekly || {};

        Object.values(loggedAM).forEach(status => {
            if (status === 'completed') completedTasks++;
        });
        Object.values(loggedPM).forEach(status => {
            if (status === 'completed') completedTasks++;
        });
        Object.values(loggedWeekly).forEach(status => {
            if (status === 'completed') completedTasks++;
        });

        const completion = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

        adherenceData[dateStr] = {
            completion: Math.round(completion),
            morning: Object.keys(loggedAM).length > 0,
            evening: Object.keys(loggedPM).length > 0,
            weekly: Object.keys(loggedWeekly).length > 0,
        };
    });

    // Calculate consistency from the real adherence data
    const completionValues = Object.values(adherenceData).map(d => d.completion);
    const totalCompletion = completionValues.reduce((sum, current) => sum + current, 0);
    const calculatedConsistency = completionValues.length > 0 ? totalCompletion / completionValues.length : 0;
    const consistency = Math.round(calculatedConsistency);
    const prevConsistency = Math.floor(Math.random() * (consistency - (consistency - 10)) + (consistency - 10)); // Mock previous for now

    const consistencyMetric = {
        title: "Routine Consistency",
        value: `${consistency}%`,
        change: `${(consistency - prevConsistency).toFixed(0)}%`,
        changeType: consistency >= prevConsistency ? "positive" : "negative",
        icon: "Calendar",
        description: "Daily routine adherence for the selected period",
    };
    
    // Other metrics can still be random for demo purposes
    const score = (Math.random() * (8.5 - 6.5) + 6.5).toFixed(1);
    const prevScore = (Math.random() * (score - (score - 1)) + (score - 1)).toFixed(1);
    const improvement = Math.floor(Math.random() * (80 - 60 + 1)) + 60;
    const prevImprovement = Math.floor(Math.random() * (improvement - (improvement - 15)) + 1) + (improvement - 15);
    const effectiveness = (Math.random() * (9.0 - 7.0) + 7.0).toFixed(1);
    const prevEffectiveness = (Math.random() * (effectiveness - (effectiveness - 1.5)) + (effectiveness - 1.5)).toFixed(1);

    const progressMetrics = [
        consistencyMetric,
        {
            title: "Overall Routine Score",
            value: `${score}/10`,
            change: `${(score - prevScore).toFixed(1)}`,
            changeType: score >= prevScore ? "positive" : "negative",
            icon: "TrendingUp",
            description: "Your routine effectiveness score over time.",
        },
        {
            title: "Skin Improvement",
            value: `${improvement}%`,
            change: `${(improvement - prevImprovement).toFixed(0)}%`,
            changeType: improvement >= prevImprovement ? "positive" : "negative",
            icon: "Sparkles",
            description: "Visible improvements in your skin's health.",
        },
        {
            title: "Product Effectiveness",
            value: `${effectiveness}/10`,
            change: `${(effectiveness - prevEffectiveness).toFixed(1)}`,
            changeType: effectiveness >= prevEffectiveness ? "positive" : "negative",
            icon: "Star",
            description: "How well your current products are working.",
        },
    ];

    const routineScoreData = dateSeries.map(d => ({ date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), value: parseFloat((Math.random() * (8.5 - 5.5) + 5.5).toFixed(1)) }));
    const skinConcernData = dateSeries.map((d, i) => ({ date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), value: 70 - i * 0.2 + Math.random() * 5 }));

    const goalsData = goals.map(goal => ({
        ...goal,
        progress: Math.min(goal.progress || 0, 100),
        status: goal.progress >= 100 ? 'completed' : 'on-track'
    }));

    const hasProgressData = Object.keys(progress).length > 0;
    const insightsData = [];
    if (hasProgressData) {
        insightsData.push({
            id: 1,
            type: 'improvement',
            title: 'Great Job on Consistency!',
            description: `You've maintained a ${consistency}% routine adherence. Keep it up!`,
            date: '1 day ago',
            priority: 'high',
            actions: ['Continue current routine'],
        });

        if (consistency < 75) {
            insightsData.push({
                id: 2,
                type: 'recommendation',
                title: 'Improve Your Routine Consistency',
                description: 'Try setting reminders to help you stay on track with your AM and PM routines.',
                date: 'now',
                priority: 'medium',
                actionButtons: [{ label: 'Set Reminder', variant: 'default', icon: 'Bell', actionId: 'set_reminder' }]
            });
        }
    } else {
        insightsData.push({
            id: 'no-data',
            type: 'recommendation',
            title: 'Start Tracking Your Progress',
            description: 'Log your daily routine to start generating personalized insights and track your skin health journey.',
            date: 'now',
            priority: 'high',
            actionButtons: [{ label: 'Log Routine', variant: 'default', icon: 'Plus', actionId: 'log-routine' }]
        })
    }

    return {
        progressMetrics,
        routineScoreData,
        skinConcernData,
        adherenceData,
        goalsData,
        insightsData
    };
};

export { processUserDataForDashboard };
